export const navigationPerformanceEvent = 'academy:navigation-performance';
export const navigationPrefetchEvent = 'academy:navigation-prefetch';

export type NavigationCacheStatus =
  'none' | 'miss' | 'fresh' | 'stale' | 'refresh' | 'deduplicated';

export type NavigationColdSource =
  | 'none'
  | 'memory-miss'
  | 'memory-cache'
  | 'intent-prefetched'
  | 'idle-prefetched'
  | 'navigation-handoff'
  | 'session-hydrated';

export type NavigationPerformancePhase =
  | 'navigation-start'
  | 'authorization'
  | 'code-load'
  | 'data-start'
  | 'request'
  | 'request-complete'
  | 'loader-visible'
  | 'continuity-visible'
  | 'data-complete'
  | 'meaningful-content'
  | 'cancelled';

export type NavigationPerformanceDetail = {
  schemaVersion: 2;
  navigationId: string;
  route: string;
  phase: NavigationPerformancePhase;
  elapsedMs: number;
  requestCount: number;
  dataRequestCount: number;
  authorizationRequestCount: number;
  cacheStatus: NavigationCacheStatus;
  coldSource: NavigationColdSource;
  authorizationMs?: number;
  codeLoadMs?: number;
  dataLoadMs?: number;
  meaningfulContentMs?: number;
  ttfbMs?: number;
  responseBytes?: number;
  loaderVisibleMs?: number;
  continuityVisibleMs?: number;
  continuityKind?: 'previous-content' | 'skeleton';
  outcome?: 'success' | 'error';
  cancelled?: true;
};

type NavigationState = {
  id: string;
  route: string;
  startedAt: number;
  requestCount: number;
  dataRequestCount: number;
  authorizationRequestCount: number;
  meaningfulRecorded: boolean;
  responseBytes: number;
  responseBytesObserved: boolean;
  ttfbTotalMs: number;
  ttfbObserved: boolean;
};

export type NavigationPrefetchTrigger = 'pointer' | 'keyboard' | 'touch' | 'idle' | 'navigation';
export type NavigationPrefetchPhase =
  'start' | 'completed' | 'hit' | 'in-flight-hit' | 'cancelled' | 'failed' | 'wasted' | 'skipped';

export type NavigationPrefetchDetail = {
  schemaVersion: 1;
  route: string;
  trigger: NavigationPrefetchTrigger;
  phase: NavigationPrefetchPhase;
  elapsedMs: number;
  requestCount: number;
  responseBytes?: number;
  skipReason?: 'offline' | 'save-data' | 'slow-connection' | 'ineligible' | 'budget';
};

const staticRoutes = new Set([
  '/',
  '/account',
  '/account/security',
  '/admin/access/accounts',
  '/admin/privacy',
  '/academy/settings',
  '/academy/retention',
  '/academy/policies',
  '/academy/lifecycles',
  '/assessment',
  '/curriculum/plans',
  '/curriculum/catalogs',
  '/curriculum/resources',
  '/curriculum/rubrics',
  '/deliveries',
  '/groups',
  '/learning',
  '/messages',
  '/operations/audit',
  '/operations/integrity',
  '/operations/jobs',
  '/operations/outbox',
  '/people',
  '/people/guardians',
  '/privacy',
  '/privacy/safety',
  '/schedule',
  '/studio/assets',
  '/studio/presentations',
  '/studio/templates',
]);

const dynamicRoutes: ReadonlyArray<readonly [RegExp, string]> = [
  [/^\/people\/teachers\/[^/]+$/, '/people/teachers/:id'],
  [/^\/people\/students\/[^/]+$/, '/people/students/:id'],
  [/^\/people\/guardians\/[^/]+$/, '/people/guardians/:id'],
  [/^\/groups\/[^/]+$/, '/groups/:id'],
  [/^\/curriculum\/plans\/[^/]+$/, '/curriculum/plans/:id'],
  [/^\/curriculum\/resources\/[^/]+$/, '/curriculum/resources/:id'],
  [/^\/sessions\/[^/]+$/, '/sessions/:id'],
  [/^\/assignments\/[^/]+$/, '/assignments/:id'],
  [/^\/studio\/presentations\/versions\/[^/]+\/edit$/, '/studio/presentations/versions/:id/edit'],
  [/^\/studio\/presentations\/versions\/[^/]+$/, '/studio/presentations/versions/:id'],
  [/^\/studio\/presentations\/[^/]+$/, '/studio/presentations/:id'],
  [/^\/studio\/templates\/[^/]+$/, '/studio/templates/:id'],
  [/^\/deliveries\/[^/]+$/, '/deliveries/:id'],
  [/^\/learn\/[^/]+$/, '/learn/:id'],
  [/^\/meetings\/[^/]+$/, '/meetings/:id'],
  [/^\/recordings\/[^/]+$/, '/recordings/:id'],
  [/^\/learning\/tasks\/[^/]+$/, '/learning/tasks/:id'],
  [/^\/learning\/progress\/[^/]+$/, '/learning/progress/:id'],
  [/^\/messages\/[^/]+$/, '/messages/:id'],
];

const now = () => (typeof performance === 'undefined' ? Date.now() : performance.now());

const duration = (milliseconds: number) => Math.max(0, Math.round(milliseconds * 10) / 10);

export const sanitizeNavigationRoute = (route: string) => {
  let pathname = '/';
  try {
    pathname = new URL(route, 'https://navigation.invalid').pathname;
  } catch {
    return '/unknown';
  }
  const normalized = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
  if (staticRoutes.has(normalized)) return normalized;
  for (const [pattern, template] of dynamicRoutes) {
    if (pattern.test(normalized)) return template;
  }
  const firstSegment = normalized.split('/').find((segment) => segment.length > 0);
  return firstSegment ? `/${firstSegment}/:path` : '/unknown';
};

let sequence = 0;
let activeNavigation: NavigationState | null = null;

const emit = (
  navigation: NavigationState,
  phase: NavigationPerformancePhase,
  cacheStatus: NavigationCacheStatus,
  fields: Partial<NavigationPerformanceDetail> = {},
) => {
  const detail: NavigationPerformanceDetail = {
    schemaVersion: 2,
    navigationId: navigation.id,
    route: navigation.route,
    phase,
    elapsedMs: duration(now() - navigation.startedAt),
    requestCount: navigation.requestCount,
    dataRequestCount: navigation.dataRequestCount,
    authorizationRequestCount: navigation.authorizationRequestCount,
    cacheStatus,
    coldSource: 'none',
    ...fields,
  };
  if (typeof window !== 'undefined' && typeof window.CustomEvent === 'function') {
    window.dispatchEvent(
      new window.CustomEvent<NavigationPerformanceDetail>(navigationPerformanceEvent, { detail }),
    );
  }
  return detail;
};

export const startNavigationTelemetry = (route: string) => {
  const startedAt = now();
  activeNavigation = {
    id: `navigation-${Date.now()}-${++sequence}`,
    route: sanitizeNavigationRoute(route),
    startedAt,
    requestCount: 0,
    dataRequestCount: 0,
    authorizationRequestCount: 0,
    meaningfulRecorded: false,
    responseBytes: 0,
    responseBytesObserved: false,
    ttfbTotalMs: 0,
    ttfbObserved: false,
  };
  emit(activeNavigation, 'navigation-start', 'none');
  return activeNavigation.id;
};

const navigationForRoute = (route: string) => {
  const sanitized = sanitizeNavigationRoute(route);
  if (activeNavigation?.route !== sanitized) startNavigationTelemetry(route);
  if (!activeNavigation) throw new Error('Navigation telemetry could not be initialized.');
  return activeNavigation;
};

export const beginNavigationRequest = (category: 'data' | 'authorization' = 'data') => {
  const navigation = activeNavigation;
  const startedAt = now();
  if (!navigation || navigation.meaningfulRecorded) {
    return { complete: (_response?: Response) => undefined };
  }
  navigation.requestCount += 1;
  if (category === 'authorization') navigation.authorizationRequestCount += 1;
  else navigation.dataRequestCount += 1;
  emit(navigation, 'request', 'none');
  let completed = false;
  return {
    complete: (response?: Response) => {
      if (completed) return;
      completed = true;
      const ttfbMs = duration(now() - startedAt);
      navigation.ttfbTotalMs += ttfbMs;
      navigation.ttfbObserved = true;
      const contentLength = response?.headers.get('content-length');
      const parsedBytes = contentLength === null ? Number.NaN : Number(contentLength);
      if (Number.isFinite(parsedBytes) && parsedBytes >= 0) {
        navigation.responseBytes += parsedBytes;
        navigation.responseBytesObserved = true;
      }
      emit(navigation, 'request-complete', 'none', {
        ttfbMs,
        ...(Number.isFinite(parsedBytes) && parsedBytes >= 0 ? { responseBytes: parsedBytes } : {}),
      });
    },
  };
};

export const recordNavigationRequest = (category: 'data' | 'authorization' = 'data') => {
  void beginNavigationRequest(category);
};

export const recordNavigationAuthorization = (authorizationMs: number) => {
  if (!activeNavigation) return;
  emit(activeNavigation, 'authorization', 'none', {
    authorizationMs: duration(authorizationMs),
  });
};

export const beginNavigationCodeLoad = () => {
  const navigation = activeNavigation;
  const startedAt = now();
  let recorded = false;
  return () => {
    if (!navigation || recorded) return;
    recorded = true;
    emit(navigation, 'code-load', 'none', { codeLoadMs: duration(now() - startedAt) });
  };
};

export const beginNavigationDataLoad = (
  route: string,
  cacheStatus: NavigationCacheStatus,
  coldSource: NavigationColdSource = cacheStatus === 'miss' ? 'memory-miss' : 'memory-cache',
) => {
  const navigation = navigationForRoute(route);
  const startedAt = now();
  let completed = false;
  let loaderVisibleAt: number | null = null;
  let continuityVisibleAt: number | null = null;
  let continuityKind: NavigationPerformanceDetail['continuityKind'];
  emit(navigation, 'data-start', cacheStatus, { coldSource });

  const visibilityFields = () => ({
    coldSource,
    ...(loaderVisibleAt === null ? {} : { loaderVisibleMs: duration(now() - loaderVisibleAt) }),
    ...(continuityVisibleAt === null
      ? {}
      : {
          continuityVisibleMs: duration(now() - continuityVisibleAt),
          ...(continuityKind ? { continuityKind } : {}),
        }),
  });

  return {
    loaderVisible: () => {
      if (loaderVisibleAt !== null) return;
      loaderVisibleAt = now();
      emit(navigation, 'loader-visible', cacheStatus, { coldSource });
    },
    continuityVisible: (kind: NonNullable<NavigationPerformanceDetail['continuityKind']>) => {
      if (continuityVisibleAt !== null) return;
      continuityVisibleAt = now();
      continuityKind = kind;
      emit(navigation, 'continuity-visible', cacheStatus, { coldSource, continuityKind: kind });
    },
    meaningful: () => {
      if (navigation.meaningfulRecorded) return;
      emit(navigation, 'meaningful-content', cacheStatus, {
        ...visibilityFields(),
        meaningfulContentMs: duration(now() - navigation.startedAt),
      });
      navigation.meaningfulRecorded = true;
    },
    complete: (outcome: 'success' | 'error' = 'success') => {
      if (completed) return;
      completed = true;
      emit(navigation, 'data-complete', cacheStatus, {
        ...visibilityFields(),
        dataLoadMs: duration(now() - startedAt),
        ...(navigation.ttfbObserved ? { ttfbMs: duration(navigation.ttfbTotalMs) } : {}),
        ...(navigation.responseBytesObserved ? { responseBytes: navigation.responseBytes } : {}),
        outcome,
      });
    },
    cancel: () => {
      if (completed) return;
      completed = true;
      emit(navigation, 'cancelled', cacheStatus, {
        ...visibilityFields(),
        dataLoadMs: duration(now() - startedAt),
        cancelled: true,
      });
    },
  };
};

export const emitNavigationPrefetch = (
  detail: Omit<NavigationPrefetchDetail, 'schemaVersion' | 'route'> & { route: string },
) => {
  const safeDetail: NavigationPrefetchDetail = {
    schemaVersion: 1,
    ...detail,
    route: sanitizeNavigationRoute(detail.route),
  };
  if (typeof window !== 'undefined' && typeof window.CustomEvent === 'function') {
    window.dispatchEvent(
      new window.CustomEvent<NavigationPrefetchDetail>(navigationPrefetchEvent, {
        detail: safeDetail,
      }),
    );
  }
  return safeDetail;
};

export const resetNavigationTelemetryForTests = () => {
  activeNavigation = null;
  sequence = 0;
};
